import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Calendar, ChevronRight, Sparkles, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { ChatSession, Takeaway, Profile } from '../lib/supabase-types';
import { motion, AnimatePresence } from 'framer-motion';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [takeaways, setTakeaways] = useState<Takeaway[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [selectedTakeaway, setSelectedTakeaway] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/');
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profile) {
          setUserProfile(profile);

          const { data: takeawayData } = await supabase
            .from('reflections')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (takeawayData) {
            setTakeaways(takeawayData);
          }

          const { data: sessionData } = await supabase
            .from('chat_sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (sessionData) {
            setSessions(sessionData);
          }
        }
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboard();

    const takeawaysSubscription = supabase
      .channel('reflections_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'reflections',
        },
        (payload) => {
          setTakeaways(current => [payload.new as Takeaway, ...current]);
        }
      )
      .subscribe();

    return () => {
      takeawaysSubscription.unsubscribe();
    };
  }, [navigate]);

  const handleDeleteTakeaway = async (id: string) => {
    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('reflections')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setTakeaways(prev => prev.filter(t => t.id !== id));
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Error deleting takeaway:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const parseTakeawayContent = (content: string): { summary: string; reflection: string; takeaway: string } => {
    const sections = content.split('\n\n').filter(Boolean);
    return {
      summary: sections[0] || '',
      reflection: sections[1] || '',
      takeaway: sections[2] || ''
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fcf7ff] to-[#fff7fc] flex items-center justify-center">
        <div className="text-[#a85ff7] animate-pulse flex items-center space-x-2">
          <Sparkles className="h-5 w-5" />
          <span>Loading your reflections...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fcf7ff] to-[#fff7fc] pb-24">
      <header className="px-4 pt-8 pb-6 sticky top-0 bg-gradient-to-br from-[#fcf7ff] to-[#fff7fc] z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-[#a85ff7] to-[#e457b5] bg-clip-text text-transparent">
            Your Reflections
          </h1>
          <p className="text-gray-600 text-lg">
            Insights and takeaways from your conversations
          </p>
        </div>
      </header>

      {takeaways.length > 0 ? (
        <div className="max-w-4xl mx-auto px-4 space-y-12">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Reflections</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {takeaways.slice(0, 3).map((takeaway) => {
                const { summary } = parseTakeawayContent(takeaway.content);
                return (
                  <motion.div
                    key={takeaway.id}
                    whileHover={{ y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 p-6 relative group border border-purple-100/30"
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmId(takeaway.id);
                      }}
                      className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all duration-200"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(takeaway.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <p className="text-gray-900 line-clamp-3 mb-4">{summary}</p>
                    <button
                      onClick={() => setSelectedTakeaway(takeaway.id)}
                      className="text-purple-600 text-sm font-medium flex items-center hover:text-purple-700 transition-colors"
                    >
                      View Details
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">All Reflections</h2>
            <div className="space-y-4">
              {takeaways.map((takeaway) => {
                const { summary } = parseTakeawayContent(takeaway.content);
                return (
                  <motion.button
                    key={takeaway.id}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.995 }}
                    onClick={() => setSelectedTakeaway(takeaway.id)}
                    className="w-full bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 text-left relative group border border-purple-100/30"
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmId(takeaway.id);
                      }}
                      className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all duration-200"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(takeaway.created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <p className="text-gray-900 line-clamp-2 pr-12">{summary}</p>
                  </motion.button>
                );
              })}
            </div>
          </section>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center py-16">
            <p className="text-gray-600 text-lg mb-8">
              No reflections yet. Start your first conversation to begin your journey.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#a85ff7] to-[#e457b5] text-white rounded-full hover:shadow-lg transition-all duration-200 text-lg font-medium"
            >
              <MessageCircle className="h-6 w-6 mr-3" />
              Start Talking
            </button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Delete Reflection
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this reflection? This action cannot be undone.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => handleDeleteTakeaway(deleteConfirmId)}
                  disabled={isDeleting}
                  className="flex-1 py-3 px-4 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 font-medium"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  disabled={isDeleting}
                  className="flex-1 py-3 px-4 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {selectedTakeaway && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto my-8"
          >
            {takeaways.find(t => t.id === selectedTakeaway) && (
              <div className="p-8 space-y-8">
                <div className="flex items-center justify-between sticky top-0 bg-white pb-6 border-b border-gray-100">
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-4 w-4 mr-2" />
                    {new Date(takeaways.find(t => t.id === selectedTakeaway)!.created_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  <button
                    onClick={() => setSelectedTakeaway(null)}
                    className="text-gray-400 hover:text-gray-600 p-2 -mr-2 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {(() => {
                  const { summary, reflection, takeaway } = parseTakeawayContent(
                    takeaways.find(t => t.id === selectedTakeaway)!.content
                  );
                  return (
                    <div className="space-y-8">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-3">Summary</h3>
                        <p className="text-gray-900 text-lg leading-relaxed">{summary}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-3">Reflection</h3>
                        <p className="text-gray-900 text-lg leading-relaxed">{reflection}</p>
                      </div>
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6">
                        <h3 className="text-sm font-medium text-purple-700 mb-3">Key Takeaway</h3>
                        <p className="text-gray-900 text-lg leading-relaxed font-medium">{takeaway}</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;